import { Component, Input, Output, EventEmitter } from '@angular/core';
import { buildButtonClasses, type ButtonSize, type ButtonVariant } from './button-classes.util';
import type { IconButtonDensity } from '../icons/action-icon';

@Component({
  selector: 'app-button',
  template: `
    <button
      [class]="getButtonClasses()"
      [disabled]="disabled || loading"
      [type]="type"
      [attr.aria-label]="ariaLabel || null"
      [attr.aria-pressed]="pressed === null ? null : pressed"
      [attr.title]="title || ariaLabel || null"
      (click)="onButtonClick($event)">
      <!-- Bez *ngIf na ng-content; display místo třídy hidden — JIT někdy nepromítne dynamické [class.hidden]. -->
      <span
        class="inline-flex items-center justify-center gap-2"
        [style.display]="loading ? 'none' : 'inline-flex'"
      >
        <ng-content></ng-content>
      </span>
      <span
        class="inline-flex items-center justify-center"
        [style.display]="!loading ? 'none' : 'inline-flex'"
      >
        <app-spinner *ngIf="loading"></app-spinner>
      </span>
    </button>
  `,
  styleUrls: ['../icons/icon-button.scss'],
  host: {
    '[class.tt-icon-btn-host]': 'iconOnly',
    '[class.tt-icon-btn-host--toolbar]': 'iconOnly && iconDensity === "toolbar"',
    '[class.tt-icon-btn-host--row]': 'iconOnly && iconDensity === "row"',
  },
})
export class ButtonComponent {
  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'md';
  @Input() fullWidth: boolean = false;
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  /** Accessible name when the button is icon-only (or when visible text is insufficient). */
  @Input() ariaLabel = '';
  /** When set, marks a toggle in a button group (`aria-pressed`). `null` omits the attribute. */
  @Input() pressed: boolean | null = null;
  /** Native tooltip; falls back to `ariaLabel` when empty. */
  @Input() title = '';
  /** Square icon button. Header toolbars are 40px; other surfaces stay 32px. */
  @Input() iconOnly = false;
  /**
   * Empty follows context (40px on the page-header action or filter row, 32px elsewhere).
   * `toolbar` is 20px in a 40px target; `row` is 16px in a 32px target.
   */
  @Input() iconDensity: IconButtonDensity = '';

  getButtonClasses(): string {
    return buildButtonClasses({
      variant: this.variant,
      size: this.size,
      iconOnly: this.iconOnly,
      fullWidth: this.fullWidth,
    });
  }

  @Output() onClick = new EventEmitter<MouseEvent>();

  onButtonClick(event: MouseEvent): void {
    this.onClick.emit(event);
  }
}
